import React, { useState, useEffect } from "react";
import PropTypes from "prop-types";
import { Dropdown, OverlayTrigger, Tooltip } from "react-bootstrap";
import {
  buttonActionTextMapping,
  buttonActionTextMappingWithLabel,
  className,
  firstGroupBtnOrder,
  iconMappings,
  popperConfig,
  secondGroupBtnOrder,
} from "./common-components-util";
import { completeIcon, MicrosoftTeamsIcon } from "../../../../../assets/icons.jsx";
import { FluentBadge } from "../../../../../components/FluentUI/Fluent-Badge";
import FluentUiIcon from "../../../../../assets/fluent-ui-icon";
import { Loader } from "@progress/kendo-react-indicators";
import { TaskActionSkeleton } from "./tasklist-skeleton";
import { FluentTooltip } from "../../../../../components/FluentUI/Fluent-Tooltip";
import { LaunchTeamsGroupChatForTask } from "../../../../../services/api/graph-api";

const DropdownMenuWithTooltip = ({
  onClick,
  iconSrc,
  iconName,
  actionText,
  isCustomButton,
  isLiveButton,
}) => {
  const tooltipId = isLiveButton ? "" : "tooltip-1";
  return (
    <Dropdown.Item
      className={className + "-item"}
      onClick={onClick}
      aria-label={actionText}
    >
      <OverlayTrigger
        placement={isLiveButton ? "left" : "bottom"}
        flip={isLiveButton ?? false}
        delay={{ show: 150, hide: 150 }}
        overlay={
          <Tooltip
            id={tooltipId}
            className={isLiveButton ? "actionToolTip" : ""}
          >
            {actionText}
          </Tooltip>
        }
      >
        <span
          className={
            isCustomButton
              ? className + "-customstatus-name"
              : className + "-item-name"
          }
        >
          {isCustomButton ? (
            <img src={iconSrc} alt={actionText} />
          ) : (
            <div className="icon" style={{ left: "0px" }} id={iconName}></div>
          )}
          <div className={isCustomButton ? "custom-text" : "action-text"}>
            {actionText}
          </div>
        </span>
      </OverlayTrigger>
    </Dropdown.Item>
  );
};

const ActionsDropdownNew2 = ({
  setUserActionType,
  auditTrailControl,
  Permission,
  openTaskHandler,
  privileges,
  getWorkflowButtons,
  task,
  setDataItem,
  fromTaskEdit = false,
  taskLabels
}) => {
  const [buttons, setButtons] = useState({
    firstBtnGroup: [],
    secondBtnGroup: [],
    customButtons: [],
    dcEditButtons: [],
    isLoading: true,
  });
  const [isActionsDropdownVisible, setIsActionsDropdownVisible] = useState(false);

  const toIconName = (text) => text.toLowerCase().split(" ").join("-");

  const setButtonActionText = (button) => {return buttonActionTextMappingWithLabel(button, taskLabels) || button;};

  const findMatchingIcon = (name) => {
    if (!name) return completeIcon;
    const lowerCaseName = name?.toLowerCase();
    const match = iconMappings.find(({ keyword }) =>
      lowerCaseName.includes(keyword)
    );
    return match ? match.icon : completeIcon;
  };

  const getTemporaryHardcodedIcons = (name) => findMatchingIcon(name);

  const customButtons = buttons.customButtons ?? [];
  const nonTeamsCustomButtons = customButtons.filter((b) => b.Name !== "Teams Chat");
  const teamsChatButtons = customButtons.filter((b) => b.Name === "Teams Chat");
  const firstBtnGroup = buttons.firstBtnGroup ?? [];
  const secondBtnGroup = buttons.secondBtnGroup ?? [];
  const editDocBtns = buttons.editDocBtns ?? buttons.dcEditButtons ?? [];

  useEffect(() => {
    if (task?.isWorkflow) {
      if (task.Workflow?.Legacy) {
        setButtonsFunction(
          task.StatusAndButtons.WorkflowButtons,
          task.Workflow?.Statuses,
          task.DocumentTypeId !== 0 ? task.StatusAndButtons.DocumentButtons : []
        );
      } else {
        setButtonsFunction(
          task.StatusAndButtons.WorkflowButtons,
          task.Workflow?.ActionListItems,
          task.DocumentTypeId !== 0 ? task.StatusAndButtons.DocumentButtons : []
        );
      }
    } else if (!task?.isWorkflow) {
      setButtons((prev) => ({
        firstBtnGroup: [],
        secondBtnGroup: [],
        customButtons: [],
        dcEditButtons: [],
        isLoading: true,
      }));
    }
  }, [task?.isWorkflow]);

  useEffect(() => {
    if (!isActionsDropdownVisible) {
      setDataItem((prev) => ({
        ...prev,
        isWorkflow: false,
      }));
    }
  }, [isActionsDropdownVisible]);

  const setButtonsFunction = (actionButtons, customButtons, dcEditButtons) => {
    let firstGroupBtnTemp = [];
    let secondGroupBtnTemp = [];

    firstGroupBtnOrder.forEach((btnOrder) => {
      const btnFound = actionButtons?.find((b) => b === btnOrder);
      if (btnFound) firstGroupBtnTemp.push(btnFound);
    });

    secondGroupBtnOrder.forEach((btnOrder) => {
      const btnFound = actionButtons?.find((b) => b === btnOrder);
      if (btnFound) secondGroupBtnTemp.push(btnFound);
    });

    const editButtonsTemp = dcEditButtons?.filter(
      (button) => button !== "Updated Doc"
    );

    setButtons((prev) => ({
      ...prev,
      firstBtnGroup: firstGroupBtnTemp,
      secondBtnGroup: secondGroupBtnTemp,
      customButtons: customButtons,
      dcEditButtons: editButtonsTemp,
      isLoading: false,
    }));
  };

  return (
    <div className="actions-dropdown-container task-actions-dropdown">
      <Dropdown
        flip={false}
        drop="left"
        onToggle={(toggle) => {
          setIsActionsDropdownVisible(toggle);
          if (toggle) getWorkflowButtons();
        }}
      >
        <FluentTooltip
          text={`${taskLabels ?? "Task"} Action`}
          target={
            <Dropdown.Toggle className="ghost-button">
              <FluentBadge
                shape="rounded"
                appearance="tint"
                size="large"
                icon={
                  buttons.isLoading && isActionsDropdownVisible ? (
                    <Loader type="pulsing" size="small" className="p-1" />
                  ) : (
                    <FluentUiIcon
                      name={"MoreHorizontalRegular"}
                      color={"#242424"}
                      size={20}
                    />
                  )
                }
              />
            </Dropdown.Toggle>
          }
        />
        <Dropdown.Menu
          show={isActionsDropdownVisible}
          popperConfig={popperConfig}
          className="task-actions-dropdown-menu-container"
        >
          {buttons.isLoading ? (
            <TaskActionSkeleton />
          ) : (
            <React.Fragment key="dropdown-container">
              {nonTeamsCustomButtons.map((btn) => (
                <DropdownMenuWithTooltip
                  key={`custom-${btn.Name}`}
                  onClick={() => setUserActionType("custom" + btn.Name)}
                  iconSrc={getTemporaryHardcodedIcons(btn.Icon)}
                  actionText={btn.Name}
                  isCustomButton
                />
              ))}

              {nonTeamsCustomButtons.length > 0 && <Dropdown.Divider />}

              {firstBtnGroup.map((btn) => (
                <DropdownMenuWithTooltip
                  key={`first-${btn}`}
                  onClick={() => setUserActionType(btn)}
                  iconName={toIconName(btn)}
                  actionText={setButtonActionText(btn)}
                />
              ))}

              {firstBtnGroup.length > 0 && <Dropdown.Divider />}

              {task?.TaskTypeId === 1 &&
              (Permission === "View" ||
                !task?.StatusAndButtons?.WorkflowButtons?.length ||
                privileges?.["Task Management"]?.Edit === false ||
                task?.isArchived ||
                ["Approved", "Completed", "Cancelled"].includes(
                  task?.StatusAndButtons?.Status
                )) ? (
                !fromTaskEdit && (
                  <DropdownMenuWithTooltip
                    key="view-task"
                    onClick={() => openTaskHandler("view")}
                    iconName="view-task"
                    actionText={`View ${taskLabels ?? "Task"}`}
                  />
                )
              ) : (
                !fromTaskEdit && (
                  <DropdownMenuWithTooltip
                    key="edit-task"
                    onClick={() => openTaskHandler("edit")}
                    iconName="edit-task"
                    actionText={`Edit ${taskLabels ?? "Task"}`}
                  />
                )
              )}

              {secondBtnGroup.map((btn) => (
                <DropdownMenuWithTooltip
                  key={`second-${btn}`}
                  onClick={() => setUserActionType(btn)}
                  iconName={toIconName(btn)}
                  actionText={setButtonActionText(btn)}
                />
              ))}

              {secondBtnGroup.length > 0 && <Dropdown.Divider />}

              <DropdownMenuWithTooltip
                key="audit-trail"
                onClick={() => auditTrailControl(true)}
                iconName="audit-trail"
                actionText="Audit Trail"
              />

              {teamsChatButtons.map((btn) => (
                <DropdownMenuWithTooltip
                  key={`teams-${btn.Name}`}
                  onClick={() => {
                    const newTab = window.open("about:blank", "_blank");
                    LaunchTeamsGroupChatForTask({ Task: task })
                      .then((response) => {
                        const url = response?.data?.WebUrl;
                        if (url) newTab.location.href = url;
                        else newTab.close();
                      })
                      .catch(() => newTab.close());
                  }}
                  iconSrc={MicrosoftTeamsIcon}
                  actionText={btn.Name}
                  isCustomButton
                />
              ))}

              {editDocBtns.length > 0 && <Dropdown.Divider />}

              {editDocBtns.map((btn, i) => (
                <React.Fragment key={`edit-${btn}-${i}`}>
                  {btn === "Live Document" ? (
                    <DropdownMenuWithTooltip
                      onClick={() => setUserActionType(btn)}
                      iconName={`${toIconName(btn)}-dropdown`}
                      actionText={btn}
                      isLiveButton
                    />
                  ) : (
                    <Dropdown.Item
                      className="edit-dropdown-menu-item"
                      onClick={() => setUserActionType(btn)}
                    >
                      <span className="edit-dropdown-menu-item-name">
                        <div
                          className="icon"
                          style={{ left: "0px" }}
                          id={`${toIconName(btn)}-dropdown`}
                        ></div>
                        <div className="action-text">{btn}</div>
                      </span>
                    </Dropdown.Item>
                  )}
                </React.Fragment>
              ))}
            </React.Fragment>
          )}
        </Dropdown.Menu>
      </Dropdown>
    </div>
  );
};

DropdownMenuWithTooltip.propTypes = {
  onClick: PropTypes.func,
  iconSrc: PropTypes.string,
  iconName: PropTypes.string,
  actionText: PropTypes.string,
  isCustomButton: PropTypes.bool,
  isLiveButton: PropTypes.bool,
};

ActionsDropdownNew2.propTypes = {
  setUserActionType: PropTypes.func,
  auditTrailControl: PropTypes.func.isRequired,
  Permission: PropTypes.string.isRequired,
  openTaskHandler: PropTypes.func.isRequired,
  privileges: PropTypes.shape({
    "Task Management": PropTypes.shape({
      Edit: PropTypes.bool.isRequired,
    }),
  }),
  getWorkflowButtons: PropTypes.func.isRequired,
  task: PropTypes.object.isRequired,
  setDataItem: PropTypes.func.isRequired,
  fromTaskEdit: PropTypes.bool,
};

ActionsDropdownNew2.defaultProps = {
  privileges: null,
};

export default ActionsDropdownNew2;
